import { memo, useCallback, useMemo, type FC } from "react";

import { EnvironmentStatusDot } from "@/components/environments/EnvironmentStatusDot";
import { CommandEmpty, CommandGroup, CommandItem, CommandLoading } from "@/components/flexkit/Command";
import { useEnvironmentName } from "@/hooks/use-environment-name";
import { useEnvironmentsByProjects, type EnvironmentsGroup } from "@/hooks/use-environments-by-projects";
import { type PlainAgentExecution, useGroupedAgentExecutions } from "@/queries/agent-queries";
import type { PlainEnvironment } from "@/queries/environment-queries";
import { useProject } from "@/queries/project-queries";
import { mostRecentAgentExecution } from "@/utils/agent-executions";
import { getProjectDisplayName } from "@/utils/environment";
import { gitStatusChangesCount, formatChangesCount } from "@/utils/git-status";
import { EnvironmentPhase, ListEnvironmentsRequest_ArchivalStatus } from "gitpod-next-api/gitpod/v1/environment_pb";

type AgentExecutionsByEnvironment = Map<string, PlainAgentExecution[]>;

import type { NavigateFunction } from "react-router-dom";

type EnvironmentsPageProps = {
    runCommand: (fn: () => void) => void;
    navigate: NavigateFunction;
};

/** Phases considered "active" — environments the user likely wants quick access to. */
const ACTIVE_PHASES = new Set([
    EnvironmentPhase.RUNNING,
    EnvironmentPhase.STARTING,
    EnvironmentPhase.CREATING,
    EnvironmentPhase.UPDATING,
]);

function isActiveEnvironment(env: PlainEnvironment): boolean {
    const phase = env.status?.phase ?? EnvironmentPhase.UNSPECIFIED;
    return ACTIVE_PHASES.has(phase);
}

export const EnvironmentsPage: FC<EnvironmentsPageProps> = memo(function EnvironmentsPage({ runCommand, navigate }) {
    const { data: groups, isLoading: isLoadingEnvs } = useEnvironmentsByProjects({
        archivalStatus: ListEnvironmentsRequest_ArchivalStatus.ACTIVE,
    });
    const allEnvironmentIds = useMemo(() => groups.flatMap((g) => g.environments.map((env) => env.id)), [groups]);
    const { data: groupedAgentExecutions, isPending: isLoadingExecutions } =
        useGroupedAgentExecutions(allEnvironmentIds);

    if (isLoadingEnvs || isLoadingExecutions) {
        return <CommandLoading>Loading environments...</CommandLoading>;
    }

    const agentExecutionsByEnvironment = groupedAgentExecutions?.environmentScoped ?? new Map();

    if (groups.length === 0) {
        return <CommandEmpty>No environments found.</CommandEmpty>;
    }

    return (
        <>
            {groups.map((group) => (
                <ProjectGroup
                    key={group.projectId || group.repoFullName || "scratch"}
                    group={group}
                    agentExecutionsByEnvironment={agentExecutionsByEnvironment}
                    runCommand={runCommand}
                    navigate={navigate}
                />
            ))}
        </>
    );
});

/**
 * Flat list of running/active environments shown in the root command palette
 * view (before the user types a search query). Sorted with running first.
 */
export const ActiveEnvironmentsList: FC<EnvironmentsPageProps> = memo(function ActiveEnvironmentsList({
    runCommand,
    navigate,
}) {
    const { data: groups, isLoading: isLoadingEnvs } = useEnvironmentsByProjects({
        archivalStatus: ListEnvironmentsRequest_ArchivalStatus.ACTIVE,
    });

    const environmentIds = useMemo(() => groups.flatMap((g) => g.environments.map((env) => env.id)), [groups]);
    const { data: groupedAgentExecutions, isPending: isLoadingExecutions } = useGroupedAgentExecutions(environmentIds);

    const activeEnvironments = useMemo(() => {
        const allEnvs = groups.flatMap((g) => g.environments);
        // Sort: running/active first, then stopped
        return allEnvs.sort((a, b) => {
            const aActive = isActiveEnvironment(a) ? 0 : 1;
            const bActive = isActiveEnvironment(b) ? 0 : 1;
            return aActive - bActive;
        });
    }, [groups]);

    if (isLoadingEnvs || isLoadingExecutions) {
        return null; // Don't show loading state in root view to avoid flicker
    }

    if (activeEnvironments.length === 0) {
        return null;
    }

    const agentExecutionsByEnvironment = groupedAgentExecutions?.environmentScoped ?? new Map();

    return (
        <CommandGroup heading="Environments">
            {activeEnvironments.map((env) => (
                <EnvironmentItem
                    key={env.id}
                    environment={env}
                    agentExecutions={agentExecutionsByEnvironment.get(env.id) ?? []}
                    runCommand={runCommand}
                    navigate={navigate}
                />
            ))}
        </CommandGroup>
    );
});

const ProjectGroup: FC<{
    group: EnvironmentsGroup;
    agentExecutionsByEnvironment: AgentExecutionsByEnvironment;
    runCommand: (fn: () => void) => void;
    navigate: NavigateFunction;
}> = ({ group, agentExecutionsByEnvironment, runCommand, navigate }) => {
    const { data: project } = useProject(group.projectId);
    const heading = group.projectId ? getProjectDisplayName(project) : group.repoFullName || "From scratch";

    return (
        <CommandGroup heading={heading}>
            {group.environments.map((env) => (
                <EnvironmentItem
                    key={env.id}
                    environment={env}
                    agentExecutions={agentExecutionsByEnvironment.get(env.id) ?? []}
                    runCommand={runCommand}
                    navigate={navigate}
                />
            ))}
        </CommandGroup>
    );
};

const EnvironmentItem: FC<{
    environment: PlainEnvironment;
    agentExecutions: PlainAgentExecution[];
    runCommand: (fn: () => void) => void;
    navigate: NavigateFunction;
}> = ({ environment, agentExecutions, runCommand, navigate }) => {
    const agentExecution = useMemo(() => mostRecentAgentExecution(agentExecutions), [agentExecutions]);
    const name = useEnvironmentName(environment, agentExecution);
    const git = environment.status?.content?.git;
    const changesCount = gitStatusChangesCount(git);
    const hasChanges = changesCount !== null && changesCount > 0;
    const handleSelect = useCallback(
        () => runCommand(() => navigate(`/details/${environment.id}`)),
        [runCommand, navigate, environment.id],
    );

    return (
        <CommandItem value={`${environment.id} ${name}`} onSelect={handleSelect}>
            <span className="flex size-4 shrink-0 items-center justify-center">
                <EnvironmentStatusDot env={environment} size="sm" />
            </span>
            <span className="truncate">{name}</span>
            {hasChanges && (
                <span className="ml-auto inline-flex h-4 items-center rounded-full bg-surface-brand px-1.5 font-mono text-[10px] font-medium leading-none text-content-brand dark:bg-content-brand/25 dark:text-surface-brand/80">
                    {formatChangesCount(changesCount)}
                </span>
            )}
        </CommandItem>
    );
};
